import {GitPullRequestClosedIcon} from '@primer/octicons-react'
import {Link} from '@primer/react'

import {ForYouItem} from '../shared/ForYouItem'
import {useHeaderPageData} from '../../../../page-data/loaders/use-header-page-data'
import {PullRequestState} from '../../../../types'
import type {StackData} from '../../../../page-data/payloads/header'

export type StackPullRequest = StackData['pulls'][number]

/**
 * Finds the closest closed downstack pull request, if any. `stack.pulls` is ordered head to
 * base, so downstack entries are the ones after the current pull request in the array.
 */
export function findClosedDownstackPullRequest(
  stack: StackData | null | undefined,
  pullRequestNumber: number,
): StackPullRequest | undefined {
  if (!stack) return undefined

  const currentIndex = stack.pulls.findIndex(pull => pull.number === pullRequestNumber)
  if (currentIndex === -1) return undefined

  return stack.pulls.slice(currentIndex + 1).find(pull => pull.state === PullRequestState.Closed)
}

export function shouldShowStackClosed(stack: StackData | null | undefined, pullRequestNumber: number): boolean {
  return findClosedDownstackPullRequest(stack, pullRequestNumber) !== undefined
}

export function StackClosed() {
  const {pullRequest, stack} = useHeaderPageData()
  const closedPullRequest = findClosedDownstackPullRequest(stack, pullRequest.number)

  if (!closedPullRequest) return null

  return (
    <ForYouItem
      itemId="stack-closed"
      visual={{kind: 'icon', icon: GitPullRequestClosedIcon, tone: 'danger', glyphSize: 12}}
      title="A previous stacked PR was closed"
      subText={
        <>
          Reopen or remove{' '}
          <Link inline href={closedPullRequest.url} className="Link--inTextBlock">
            #{closedPullRequest.number}
          </Link>{' '}
          before merging this pull request.
        </>
      }
    />
  )
}
